import React from 'react';
import { toast } from 'react-toastify';
import { createProduct } from '../../sp-firebase/productFB';
import { Product } from '../../sp-types/productType';
import { IProductFilters } from './ProductDashboard/Components/ProductFilterSection';
import { Timestamp } from 'firebase/firestore';
import { comment } from '../../sp-types/FutureOrderType';

export const isProductValidToSave = (product: Product): boolean => {
  const { category, id, ingredients, measureType, name, subCategory } = product;
  const isFieldMissing = [category, id, measureType, name, subCategory].some((field) => !field);
  const hasInvalidIngredient = ingredients.some((item) => item.weightInProduct === 0);
  return !isFieldMissing && ingredients.length > 0 && !hasInvalidIngredient;
};

export const saveProductOnDb = async (
  product: Product,
  accountId: string,
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>,
  navigateFunction?: (_productUuid?: string) => void,
) => {
  setLoading && setLoading(true);
  try {
    const { success, id: newProductUuid } = await createProduct(accountId, {
      comments: [],
      ...product,
      status: 'completed',
      updatedAt: Timestamp.now(),
    });
    setLoading && setLoading(false);
    navigateFunction && success && navigateFunction(newProductUuid || '');
  } catch {
    toast.info('product save failed - try again');
  }
};

export const emptyProduct: Product = {
  category: '',
  currentPrice: 0,
  filesUrl: [],
  comments: [],
  id: '',
  ingredients: [],
  measureType: 'Unit',
  name: '',
  priceHistory: [],
  subCategory: '',
  unitWeight: 0,
  status: 'underwriting',
  updatedAt: Timestamp.now(),
};

export const onSubmitProductContractFile = async (
  accountId: string,
  fileUrls: string[],
  comments: comment[],
  routeFunction: () => void,
) => {
  const getFormattedDate = () => {
    const now = new Date();

    const datePart = now
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
      .replace(/\//g, '');

    const timePart = now
      .toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      .replace(/:/g, '');

    return `${datePart}${timePart}`;
  };

  try {
    await createProduct(accountId, {
      ...emptyProduct,
      filesUrl: fileUrls,
      comments,
      id: getFormattedDate(),
      status: 'pending',
    });

    // const notification = createProductNotification(accountId, fileUrls?.[0], creation.id || '');
    // await addNotification(notification);

    routeFunction();
  } catch {
    toast.error('submitting file failed. try again');
  }
};

// const createProductNotification = (accountId: string, fileUrl: string, fileId: string): DashboardNotification => ({
//   type: 'info',
//   accountId,
//   text: 'Product file uploaded',
//   date: Timestamp.fromDate(new Date()),
//   descriptionText: `customer uploaded product file id: ${fileId}, admin, please add its props.`,
//   actionText: 'download file',
//   actionUrl: fileUrl,
// });

export const filterProductList = (productList: Product[], filters: IProductFilters): Product[] => {
  const { id, category, measureType, name, subCategory, materials } = filters;
  return productList.filter((product) => {
    const matchesId = id ? product.id.includes(id) : true;
    const matchesName = name ? product.name.includes(name) : true;
    const matchesMeasureType = measureType ? product.measureType === measureType : true;
    const matchesCategory = category ? product.category === category : true;
    const matchesSubCategory = subCategory ? product.subCategory === subCategory : true;
    const hasMaterial = materials?.length
      ? product.ingredients.some((ingredient) => materials.includes(ingredient.materialID))
      : true;
    const isValid = isProductValidToSave(product);
    const hasUUID = product.uuid !== undefined;

    return (
      matchesId &&
      matchesName &&
      matchesMeasureType &&
      matchesCategory &&
      matchesSubCategory &&
      hasMaterial &&
      isValid &&
      hasUUID
    );
  });
};

export type ProductScreensType = 'attachFiles' | 'modifyProduct' | 'pendingScreen';
